import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { useApi } from '@/api/useApi.tsx';
import { CallState, QUESTION_ACCEPT_DURATION, useSolveQuestion } from '@/context/solve-question-context';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { NavigationPath } from '@/router/navigation-path.ts';
import SolveQuestionDefaultState from '@/screens/solver/solve-question/states/default.tsx';
import SolveQuestionQuestionsState from '@/screens/solver/solve-question/states/questions.tsx';
import { DialogModal } from '@/components';
import { jsonTryParse, jsonTryStringify, purify } from '@/utility';
import { ImageCropModal, PassedQuestionsModal, ReportModal, SolutionVideoModal, SolutionVideoModalV2, SolveBottomSheet } from '@/screens/solver/_components';
import 'yet-another-react-lightbox/styles.css';
import Lightbox from 'yet-another-react-lightbox';
import * as yup from 'yup';
import MobileDirection from '@/screens/solver/solve-question/states/mobile-direction.tsx';
import { useAuthStore } from '@/zustand/auth/store.ts';
import { toast } from 'react-toastify';
import { LocalStorageKeys } from '@/constants/local-storage-keys.ts';
import { PostMessage } from '@/constants/post-message.ts';
import { isWebView, sendMessageToWebView } from '@/utility/webview.ts';
import WebDirection from '@/screens/solver/solve-question/states/web-direction.tsx';
import PassedQuestions from '@/screens/solver/solve-question/states/passed-questions.tsx';
import { Tooltip } from 'react-tooltip';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { useMeeting } from '@/context/meeting-context';

const passSchema = yup.object({
	passQuestion: yup.string().required('Lütfen bir iptal nedeni seçin')
});

const isDesktop = !isWebView();

const SolveQuestionContent = () => {
	const { value: isActiveSolverV2UploadScreen } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_V2_UPLOAD_SCREEN, false);

	const { data: authData } = useAuthStore();

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { SolverQuestionControllerApi } = useApi();

	const {
		callState,
		setCallState,
		modalContent,
		setModalContent,
		isActiveQuestionInspect,
		setIsActiveQuestionInspect,
		setIsDisabledQuestionTimer,
		questionIsAccepted,
		setQuestionIsAccepted,
		setIsOpenSolveBottomSheet,
		setIsActiveQuestionReport,
		getActiveQuestion,
		handleStartSolving: contextHandleStartSolving,
		handleRejectQuestion: contextHandleRejectQuestion,
		moveToNextQuestion,
		activeTimer,
		remainingPassedQuestionCount,
		_isActiveSolverMobileDirection,
		_isActiveSolverWebDirection,
		setCurrentQuestionTimer,
		hasQuestions,
		isResolveMode,
		resolveQueueId
	} = useSolveQuestion();

	const { isConnectToastVisible, isAppointmentAvailable } = useMeeting();

	const activeQuestion = getActiveQuestion();

	const [uploadedVideoData, setUploadedVideoData] = useState(null);
	const [isVideoUploadModalOpen, setIsVideoUploadModalOpen] = useState(false);
	const [showCameraPassModal, setShowCameraPassModal] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		reset
	} = useForm({
		resolver: yupResolver(passSchema),
		mode: 'onChange'
	});

	const getDesktopTitle = () => {
		if (questionIsAccepted) {
			return 'Çözüm Bekleyen Soru';
		}

		if (!remainingPassedQuestionCount) {
			return 'Pas Hakkın Şimdilik Doldu!';
		}

		return 'Hazırsan Öğrenci Soruları Seni Bekliyor!';
	};

	const renderTitleSection = () => {
		if (isDesktop) {
			return <h1>{getDesktopTitle()}</h1>;
		}

		if (!questionIsAccepted && callState === CallState.DEFAULT) {
			return <h1>Hazırsan Öğrenci Soruları Seni Bekliyor!</h1>;
		}

		if (questionIsAccepted) {
			return <h1>Çözüm Bekleyen Soru</h1>;
		}

		if (!remainingPassedQuestionCount) {
			return <h1>Pas Hakkın Şimdilik Doldu!</h1>;
		}

		return (
			<div className="mobile-questions-header">
				<button
					onClick={onBackDefaultState}
					className="questions-back-button">
					<i className="icons-back-arrow" />
				</button>
				<h1>Sorular</h1>
				<div />
			</div>
		);
	};

	const getMainDescription = () => {
		if (questionIsAccepted) {
			return 'Kabul ettiğiniz soruyu size verilen süre bitmeden çözmeniz gerekmektedir.';
		}

		if (!remainingPassedQuestionCount && hasQuestions) {
			return "Yeni pas hakkı için saat 09:00'u bekleyebilirsin veya pas geçtiğin sorulardan çözerek yeni pas hakları kazanabilirsin.";
		}

		if (!remainingPassedQuestionCount) {
			return "Yeni pas hakkı için saat 09:00'u bekleyebilirsin.";
		}

		return 'Soruyu çöz, gönder, değerlendirilsin ve kazancın anında yansısın!';
	};

	const getModalMessage = () => {
		const action = questionIsAccepted ? 'iptal ederseniz' : 'redderseniz';
		return `Bu soruyu ${action} <b/>1 pas hakkınız kullanılır.</b> Günlük 10 pas hakkınız vardır.`;
	};

	const getRejectButtonText = () => {
		return questionIsAccepted ? 'İptal Et' : 'Reddet';
	};

	const { data: solverPassQuestion } = useQueryProvider([QUERY_KEYS.solverPassQuestion], () => SolverQuestionControllerApi.getPassReasons(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false
	});

	const createPassedQuestion = useMutationProvider(body => SolverQuestionControllerApi.createPassedQuestion(body), {
		onError: error => {
			setCallState(CallState.DEFAULT);
			setShowCameraPassModal(false);
			moveToNextQuestion(false);
			ExceptionManager.errorHandler(error);
		},
		onSuccess: () => {
			queryClient.refetchQueries({
				queryKey: [QUERY_KEYS.getRemainingPassedQuestionCount]
			});
			toast.error('Günde 10 soru iptal etme hakkınız bulunmaktadır. Daha fazla soru çözebilmek için haklarınızı dikkatli kullanınız.');
			setCallState(CallState.DEFAULT);
			setShowCameraPassModal(false);
			moveToNextQuestion(false);
		}
	});

	useEffect(() => {
		window.authStore = {
			getState: () => ({ authData: authData })
		};

		window.activeQuestionData = activeQuestion;

		return () => {
			delete window.authStore;
			delete window.activeQuestionData;
		};
	}, [authData, activeQuestion]);

	useEffect(() => {
		const handleMessage = event => {
			let data;

			if (typeof event.data === 'string') {
				data = JSON.parse(event.data);
			} else if (typeof event.data === 'object' && event.data !== null) {
				data = event.data;
			} else {
				return;
			}

			if (data && data.type === PostMessage.CROPPED_IMAGE && data.imageUri) {
				const timerInfo = activeTimer?.getTimerInfo();

				const whiteboardData = {
					interactionId: activeQuestion?.interactionId,
					questionId: activeQuestion?.questionId,
					accessToken: authData?.access_token || null,
					isResolveMode,
					resolveQueueId,
					questionInfo: {
						grade: activeQuestion?.curriculumName,
						subject: activeQuestion?.subject,
						topic: activeQuestion?.curriculumElementUnitName
					},
					activeQuestion: {
						...activeQuestion,
						questionImage: data.imageUri,
						remainingTime: timerInfo?.remainingTime || 0,
						isTimerActive: activeTimer?.isActive || false,
						timerCreatedAt: timerInfo?.createdAt,
						timerTimeoutAt: timerInfo?.timeoutAt
					},
					timestamp: Date.now()
				};

				sendMessageToWebView({
					type: PostMessage.WHITE_BOARD_DATA,
					data: whiteboardData
				});

				navigate(NavigationPath.solverWhiteBoard, {
					state: whiteboardData
				});
			} else if (data && data.type === PostMessage.CAMERA_PASS_SELECTED && data.passReasonId) {
				handleCameraPassSubmit({ passQuestion: data.passReasonId.toString() });
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [navigate, activeQuestion, activeTimer]);

	useEffect(() => {
		sendMessageToWebView({
			type: PostMessage.SOLVER_PASS_QUESTION_DATA,
			data: solverPassQuestion
		});
	}, [solverPassQuestion]);

	const onGetQuestions = () => {
		setCallState(CallState.QUESTIONS);

		if (remainingPassedQuestionCount) {
			const saved = localStorage.getItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
			const _saved = jsonTryParse(saved);

			if (!!_saved && !!_saved.questionId) {
				const timeoutAt = new Date(_saved?.timeoutAt);
				const now = new Date();

				const remainingSeconds = Math.max(0, Math.floor((timeoutAt - now) / 1000));

				setCurrentQuestionTimer(remainingSeconds);

				if (remainingSeconds === 0) {
					localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
					localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);
				}
			} else {
				const now = new Date();
				const questionTimeoutAt = new Date(now.getTime() + 45 * 1000);

				const activeQuestionTimer = {
					questionId: activeQuestion?.questionId,
					timeoutAt: questionTimeoutAt.toISOString()
				};

				localStorage.setItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER, jsonTryStringify(activeQuestionTimer)!);

				setCurrentQuestionTimer(QUESTION_ACCEPT_DURATION);
			}
		}
	};

	const onInspectQuestion = () => setIsActiveQuestionInspect(true);
	const onClearCallState = () => {
		setCallState(CallState.DEFAULT);
		setQuestionIsAccepted(false);
		setIsDisabledQuestionTimer(false);
	};
	const onClearModalContent = () => setModalContent(null);

	const onStartSolving = () => {
		setIsDisabledQuestionTimer(true);
		setIsOpenSolveBottomSheet(true);
	};

	const onOpenSolveBottomSheet = () => {
		setIsDisabledQuestionTimer(true);
		setIsOpenSolveBottomSheet(true);
	};

	const onOpenReportQuestionModal = () => {
		setIsActiveQuestionReport(true);
	};

	const handleVideoUploadModalClose = () => {
		setIsVideoUploadModalOpen(false);
		if (uploadedVideoData && uploadedVideoData.url && uploadedVideoData.url.startsWith('blob:')) {
			URL.revokeObjectURL(uploadedVideoData.url);
		}
		setUploadedVideoData(null);

		moveToNextQuestion(false);
		setCallState(CallState.DEFAULT);
		activeTimer.clearAllTimers();
	};

	const handleVideoSubmitSuccess = () => {
		handleVideoUploadModalClose();
		moveToNextQuestion(false);
		setCallState(CallState.DEFAULT);
		activeTimer.clearAllTimers();
	};

	const handleVideoRetry = () => {
		if (uploadedVideoData?.fromCamera) {
			setIsVideoUploadModalOpen(false);
		}
	};

	const handleCameraPassSubmit = async data => {
		await createPassedQuestion.mutateAsync({
			questionId: activeQuestion?.questionId || '',
			passReasonId: parseInt(data.passQuestion)
		});
		reset();
	};

	const handleCloseCameraPassModal = () => {
		reset();
		setShowCameraPassModal(false);
	};

	const onBackDefaultState = () => {
		setCallState(CallState.DEFAULT);
	};

	const areYouStillThere = {
		title: 'Hala Orada Mısın?',
		message: 'Sana verilen sürede soruyu kabul etmedin, soru çözmeye devam etmek istiyor musun?',
		approveButtonText: 'Devam Et',
		approveButtonFunc: async () => {
			onClearModalContent();
			setIsDisabledQuestionTimer(false);
			setQuestionIsAccepted(false);
			moveToNextQuestion();
		},
		rejectButtonText: 'Çıkış Yap',
		rejectButtonFunc: async () => {
			onClearModalContent();
			onClearCallState();
			if (remainingPassedQuestionCount) {
				localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
				localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);
			}
			toast.error('Günde 10 soru reddetme hakkınız bulunmaktadır. Daha fazla soru çözebilmek için haklarınızı dikkatli kullanınız.');
		}
	};

	const areYouSure = {
		title: 'Emin Misin?',
		message: getModalMessage(),
		approveButtonText: 'Çözmeye Başla',
		approveButtonIcon: (
			<i
				className="icons-check-square"
				style={{ fontSize: 20 }}
			/>
		),
		rejectButtonIcon: (
			<i
				className="icons-close-square"
				style={{ fontSize: 20, color: 'var(--danger-text)' }}
			/>
		),
		approveButtonFunc: async () => {
			onClearModalContent();
			const activeQuestion = getActiveQuestion();

			if (!activeQuestion?.questionId || !activeQuestion?.interactionId) {
				return;
			}

			if (questionIsAccepted && activeTimer.isActive) {
				const timerInfo = activeTimer.getTimerInfo();
				if (timerInfo && timerInfo.remainingTime > 0) {
					onStartSolving();
				}
				return;
			}

			if (!questionIsAccepted) {
				await contextHandleStartSolving(activeQuestion.questionId, activeQuestion.interactionId);
			}
		},
		rejectButtonText: getRejectButtonText(),
		rejectButtonFunc: async () => {
			const activeQuestion = getActiveQuestion();
			onClearModalContent();
			await contextHandleRejectQuestion(activeQuestion.questionId);
		}
	};

	const onCompletedTimer = async () => {
		setModalContent(areYouStillThere);

		const activeQuestion = getActiveQuestion();
		if (activeQuestion?.questionId) {
			await contextHandleRejectQuestion(activeQuestion.questionId, true);
		}
	};
	const onRejectQuestion = () => setModalContent(areYouSure);

	if (_isActiveSolverMobileDirection) {
		return <MobileDirection />;
	}

	if (_isActiveSolverWebDirection) {
		return <WebDirection />;
	}

	return (
		<section className="container">
			<div className="solve-container">
				<div className="solver-info-container">
					{renderTitleSection()}

					<p>{getMainDescription()}</p>

					{!!remainingPassedQuestionCount && (
						<div className="credit-of-pass-box">
							<i
								className="icons-info-vector-bold"
								data-tooltip-id="pass-tooltip"
								data-tooltip-content="Bir soruyu reddettiğinizde 1 pas hakkı kullanılır. Günlük 10 pas hakkınız her sabah 09:00'da yenilenir. Pas geçtiğiniz soruları çözdükçe ek pas hakkı kazanırsınız."
							/>
							Kalan Pas Hakkı:
							<i className="icons-stars" />
							<span className="credit-count">{remainingPassedQuestionCount}</span>
							<Tooltip
								id="pass-tooltip"
								place="bottom-start"
							/>
						</div>
					)}
				</div>
			</div>

			{callState === CallState.PASSED_QUESTIONS ? (
				<PassedQuestions />
			) : callState === CallState.DEFAULT ? (
				<SolveQuestionDefaultState
					isSearchDisabled={isConnectToastVisible || isAppointmentAvailable}
					isAppointmentAvailable={isAppointmentAvailable}
					onGetQuestions={onGetQuestions}
				/>
			) : (
				<SolveQuestionQuestionsState
					onClearCallState={onClearCallState}
					onCompletedTimer={onCompletedTimer}
					onRejectQuestion={onRejectQuestion}
					onStartSolving={onStartSolving}
					onOpenSolveBottomSheet={onOpenSolveBottomSheet}
					onInspectQuestion={onInspectQuestion}
					onReportQuestion={onOpenReportQuestionModal}
					questionIsAccepted={questionIsAccepted}
					setQuestionIsAccepted={setQuestionIsAccepted}
					setIsDisabledQuestionTimer={setIsDisabledQuestionTimer}
				/>
			)}

			<DialogModal
				isOpen={!!modalContent}
				onClose={onClearModalContent}
				className="solver-dialog-modal-solve"
				variant="danger"
				title={modalContent?.title}
				primaryButtonText={modalContent?.approveButtonText}
				primaryButtonIcon={modalContent?.approveButtonIcon}
				secondaryButtonText={modalContent?.rejectButtonText}
				secondaryButtonIcon={modalContent?.rejectButtonIcon}
				onSecondaryClick={modalContent?.rejectButtonFunc}
				onPrimaryClick={modalContent?.approveButtonFunc}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify(modalContent?.message)} />
			</DialogModal>

			<DialogModal
				isOpen={showCameraPassModal}
				onClose={handleCloseCameraPassModal}
				className="solver-dialog-modal"
				mobileBottomSheet
				variant="danger"
				title="Soruyu İptal Et"
				primaryButtonText="İptal Et"
				secondaryButtonText="Vazgeç"
				primaryButtonActive={isValid || createPassedQuestion.isLoading}
				onSecondaryClick={handleCloseCameraPassModal}
				onPrimaryClick={handleSubmit(handleCameraPassSubmit)}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Soruyu iptal etmeniz halinde <b>1 pas hakkınız kullanılacaktır.</b> İptal işlemine devam edebilmek için lütfen iptal etme sebebini seçiniz.')} />
				<form className="report-form">
					{solverPassQuestion?.map((option, index) => (
						<div
							className="report-option"
							key={index}>
							<label className="report-option-label">
								<input
									type="radio"
									value={option?.id}
									{...register('passQuestion')}
									className="report-option-input"
								/>
								<span className="report-option-text">{option?.description}</span>
							</label>
						</div>
					))}
					{errors.passQuestion && <p className="error-message">{errors.passQuestion.message}</p>}
				</form>
			</DialogModal>

			<ReportModal />

			<SolveBottomSheet
				setUploadedVideoData={setUploadedVideoData}
				setIsVideoUploadModalOpen={setIsVideoUploadModalOpen}
			/>

			<ImageCropModal />

			{isActiveSolverV2UploadScreen ? (
				<SolutionVideoModalV2
					isOpen={isVideoUploadModalOpen}
					onClose={handleVideoUploadModalClose}
					onRetry={handleVideoRetry}
					recordedVideoData={uploadedVideoData}
					activeQuestion={activeQuestion}
					solverTimer={activeTimer}
					onSuccess={handleVideoSubmitSuccess}
					showRetryButton={false}
					isResolveMode={isResolveMode}
					resolveQueueId={resolveQueueId}
				/>
			) : (
				<SolutionVideoModal
					isOpen={isVideoUploadModalOpen}
					onClose={handleVideoUploadModalClose}
					onRetry={handleVideoRetry}
					recordedVideoData={uploadedVideoData}
					activeQuestion={activeQuestion}
					solverTimer={activeTimer}
					onSuccess={handleVideoSubmitSuccess}
					showRetryButton={false}
					isResolveMode={isResolveMode}
					resolveQueueId={resolveQueueId}
				/>
			)}

			<Lightbox
				open={isActiveQuestionInspect}
				close={() => setIsActiveQuestionInspect(false)}
				carousel={{ finite: true }}
				controller={{ closeOnBackdropClick: true }}
				render={{
					buttonPrev: () => null,
					buttonNext: () => null
				}}
				slides={[{ src: getActiveQuestion()?.questionImage, width: 920, height: 520 }]}
			/>

			<PassedQuestionsModal />
		</section>
	);
};

export default SolveQuestionContent;
